/**
 * Clinical catalogues (two-tier governance, SPEC06; Intake redesign
 * ZOY-574): three lists under one shape. History items and complaints take
 * clinic additions; the medication master list is PLATFORM-managed — a
 * clinic can hide or show a row here but never add one (the POST is gone).
 * A history item declares its intake section at creation (ZOY-570).
 */

import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, Segmented, TextField } from '@/ui/kit';
import { SelectField } from '@/ui/formkit';
import { Column, Pager, PrimaryCell, RowButton, TableSearch, WorkTable } from '@/ui/WorkTable';
import { useNumberedList } from '@/core/api/usePagedList';
import { useDebounced } from '@/core/api/useDebounced';
import type { QueryFilter } from '@/core/api/querySpec';
import {
  createClinicalItem,
  listClinicalItems,
  setClinicalItemDisabled,
  type ClinicalItemView,
  type ClinicalKind,
} from '@/core/clinic/setupApi';
import { HISTORY_SECTIONS } from '@/core/clinic/intakeApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';

export function CatalogsTab() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const authz = useAuthz();
  const manage = authz.can(PERM.catalogManage);
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<ClinicalKind>('medications');
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newSection, setNewSection] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only the two clinic-extensible lists get an add form; medications never.
  const canCreateKind = kind !== 'medications';
  const needsSection = kind === 'history-items';

  const filters = useMemo<QueryFilter[]>(
    () =>
      debounced.trim() === '' ? [] : [{ field: 'name', op: 'contains', value: debounced.trim() }],
    [debounced],
  );

  const list = useNumberedList<ClinicalItemView>({
    keyBase: ['clinical', kind, authz.clinicId],
    fetchPage: (spec) => listClinicalItems(kind, spec),
    filters,
    sort: [{ field: 'name', dir: 'asc' }],
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['clinical', kind] });
  const create = useMutation({
    mutationFn: () =>
      kind === 'history-items'
        ? createClinicalItem({
            kind,
            name: newName.trim(),
            category: newCategory.trim(),
            section: newSection,
          })
        : createClinicalItem({
            kind: 'complaints',
            name: newName.trim(),
            category: newCategory.trim(),
          }),
    onMutate: () => setError(null),
    onSuccess: () => {
      setNewName('');
      setNewCategory('');
      setNewSection('');
      invalidate();
    },
    onError: () => setError(t('cat.createFailed') as string),
  });
  const canAdd =
    canCreateKind &&
    newName.trim() !== '' &&
    newCategory.trim() !== '' &&
    (!needsSection || newSection !== '') &&
    !create.isPending;
  const toggle = useMutation({
    mutationFn: (item: ClinicalItemView) => setClinicalItemDisabled(kind, item.id, !item.disabled),
    onSuccess: invalidate,
  });

  const columns: Column[] = [
    { id: 'name', label: t('cat.name') as string, width: '2fr' },
    { id: 'status', label: t('staff.colStatus') as string, width: 'auto', tier: 'tablet-up' },
    { id: 'actions', label: t('staff.colActions') as string, width: 'auto', align: 'end' },
  ];

  const sectionLabel = (s: string | null | undefined) =>
    s && (HISTORY_SECTIONS as readonly string[]).includes(s) ? t(`intake.hs.${s}`) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxInlineSize: 760 }}>
      <p className={a.pageSub}>{t('cat.sub')}</p>
      <Segmented<ClinicalKind>
        label={t('setup.tabCatalogs') as string}
        options={[
          { value: 'medications', label: t('cat.medications') as string },
          { value: 'history-items', label: t('cat.historyItems') as string },
          { value: 'complaints', label: t('cat.complaints') as string },
        ]}
        value={kind}
        onChange={(k) => {
          setKind(k);
          setError(null);
        }}
      />
      {/* ZOY-572: clinic users must understand this list is not theirs to extend. */}
      {kind === 'medications' && <Notice kind="info">{t('cat.medPlatformNote')}</Notice>}
      {error && (
        <Notice kind="err" role="alert">
          {error}
        </Notice>
      )}
      <WorkTable<ClinicalItemView>
        columns={columns}
        rows={list.view.rows}
        rowKey={(r) => r.id}
        loading={list.loading}
        error={list.error !== null}
        onRetry={list.refetch}
        toolbar={
          <>
            <TableSearch
              value={search}
              onChange={setSearch}
              placeholder={t('cat.name') as string}
            />
            {manage && canCreateKind && (
              <span style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <TextField label={t('cat.add') as string} value={newName} onChange={setNewName} />
                <TextField
                  label={t('cat.category') as string}
                  value={newCategory}
                  onChange={setNewCategory}
                  help={t('cat.categoryHelp')}
                />
                {needsSection && (
                  <SelectField
                    label={t('cat.section') as string}
                    value={newSection}
                    onChange={setNewSection}
                    help={t('cat.sectionHelp') as string}
                    options={[
                      { value: '', label: '—' },
                      ...HISTORY_SECTIONS.map((s) => ({
                        value: s,
                        label: t(`intake.hs.${s}`) as string,
                      })),
                    ]}
                  />
                )}
                <Button variant="quiet" disabled={!canAdd} onClick={() => create.mutate()}>
                  {t('common.add')}
                </Button>
              </span>
            )}
          </>
        }
        empty={{
          title: t(debounced ? 'table.noResultsTitle' : 'cat.emptyTitle') as string,
          body: t(debounced ? 'table.noResultsBody' : 'cat.emptyBody') as string,
        }}
        renderCell={(item, col) => {
          switch (col) {
            case 'name': {
              const origin = item.source === 'platform' ? t('svc.platform') : t('svc.clinicMade');
              const section = sectionLabel(item.section);
              return (
                <PrimaryCell
                  main={item.name}
                  sub={
                    <>
                      {origin}
                      {section && <> · {section}</>}
                      {item.category && !section && <> · {item.category}</>}
                    </>
                  }
                />
              );
            }
            case 'status':
              return !item.active ? (
                <Chip kind="quiet">{t('cat.archived')}</Chip>
              ) : item.disabled ? (
                <Chip kind="quiet">{t('svc.disabledHere')}</Chip>
              ) : (
                <Chip kind="ok">{t('common.enabled')}</Chip>
              );
            case 'actions':
              // An archived row is the platform's call; the clinic toggle only
              // means anything on a live row.
              return manage && item.active ? (
                <RowButton danger={!item.disabled} onClick={() => toggle.mutate(item)}>
                  {item.disabled ? t('common.enable') : t('common.disable')}
                </RowButton>
              ) : null;
            default:
              return null;
          }
        }}
        footer={
          <Pager
            view={list.view}
            perPage={list.perPage}
            onPerPage={list.setPerPage}
            onPage={list.goTo}
          />
        }
      />
    </div>
  );
}
